import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Building2, CheckCircle2, Clock, AlertTriangle, Search, Mail, Download, TrendingUp, TrendingDown, Zap, FileWarning, BarChart3, MessageSquare, X, Sparkles, BookOpen, MoreVertical, Settings } from 'lucide-react';
import { InfoModal } from '../common/InfoModal';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { useTheme } from '../../context/ThemeContext';
import { apiGet } from '../../utils/api';
import AskJPro from './AskJPro';
import ClientPropertySidePanel from './ClientPropertySidePanel';
import EmailComposeModal from './EmailComposeModal';
import ClientEmailTemplateSettings from './ClientEmailTemplateSettings';
import SmartFilter from './SmartFilter';
import Pagination from './Pagination';
import toast from 'react-hot-toast';

const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTHS_FULL = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const STATUS_THEME = {
  completed:          { label: 'Final Report Sent', color: '#10b981', bg: 'bg-emerald-500', light: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' },
  review_complete:    { label: 'Review Complete', color: '#10b981', bg: 'bg-emerald-400', light: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
  all_received:       { label: 'All Received', color: '#3b82f6', bg: 'bg-blue-500', light: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  in_review:          { label: 'In Review', color: '#8b5cf6', bg: 'bg-violet-500', light: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300' },
  partially_received: { label: 'Partial Upload', color: '#f97316', bg: 'bg-orange-500', light: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300' },
  files_requested:    { label: 'Pending Upload', color: '#ef4444', bg: 'bg-red-500', light: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
  files_selected:     { label: 'Files Selected', color: '#6366f1', bg: 'bg-indigo-500', light: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300' },
  setup_requested:    { label: 'Setup Requested', color: '#a78bfa', bg: 'bg-violet-400', light: 'bg-violet-100 text-violet-600 dark:bg-violet-900/40 dark:text-violet-300' },
  scheduled:          { label: 'Scheduled', color: '#9ca3af', bg: 'bg-gray-400', light: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
  paused:             { label: 'Paused', color: '#6b7280', bg: 'bg-gray-500', light: 'bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-300' },
  unresponsive:       { label: 'Pending', color: '#ef4444', bg: 'bg-red-400', light: 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300' },
};

const ACTION_STATES = ['files_requested', 'partially_received', 'unresponsive'];

const MoreDropdown = ({ isDark, onExport, onGuide, onTemplates }) => {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
        <MoreVertical size={16} />
      </button>
      {open && (
        <div className={`absolute right-0 top-full mt-1 w-40 rounded-lg border shadow-lg z-20 py-1 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <button onClick={() => { onExport(); setOpen(false); }} className={`w-full text-left flex items-center gap-2 px-3 py-2 text-sm font-rasa ${isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-50'}`}>
            <Download size={14} /> Export CSV
          </button>
          <button onClick={() => { onGuide(); setOpen(false); }} className={`w-full text-left flex items-center gap-2 px-3 py-2 text-sm font-rasa ${isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-50'}`}>
            <BookOpen size={14} /> Feature Guide
          </button>
          <button onClick={() => { onTemplates(); setOpen(false); }} className={`w-full text-left flex items-center gap-2 px-3 py-2 text-sm font-rasa ${isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-50'}`}>
            <Settings size={14} /> Email Templates
          </button>
        </div>
      )}
    </div>
  );
};

const ClientAuditDashboard = () => {
  const { theme } = useTheme();
  const d = theme === 'dark';

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [year, setYear] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | attention | completed
  const [monthFilter, setMonthFilter] = useState(null);
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const [page, setPage] = useState(1);
  const [showAskJPro, setShowAskJPro] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [showTemplateSettings, setShowTemplateSettings] = useState(false);
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [clientTemplates, setClientTemplates] = useState({});
  const [composeEmail, setComposeEmail] = useState(null); // { property, contact }
  const perPage = 15;

  // Fetch available clients on mount
  useEffect(() => {
    apiGet('/client/audit_tracker/clients')
      .then(async (res) => {
        if (res.ok) {
          const list = await res.json();
          setClients(list);
          if (list.length > 0 && !selectedClientId) setSelectedClientId(String(list[0].id));
        }
      })
      .catch(() => {});
  }, []);

  const loadDashboard = useCallback(async (yr, clientId) => {
    if (!clientId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (yr) params.set('year', yr);
      params.set('client_id', clientId);
      const res = await apiGet(`/client/audit_tracker/dashboard?${params}`);
      if (!res.ok) throw new Error('Failed to load');
      const result = await res.json();
      setData(result);
      if (!yr && result.audit) setYear(String(result.audit.year));
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  }, []);

  // Reload when year or client changes
  useEffect(() => { loadDashboard(year, selectedClientId); }, [year, selectedClientId, loadDashboard]);

  // Load client email templates
  useEffect(() => {
    if (!selectedClientId) return;
    apiGet(`/client/audit_tracker/email_templates?client_id=${selectedClientId}`)
      .then((r) => r.ok ? r.json() : { templates: {} })
      .then((data) => setClientTemplates(data.templates || {}))
      .catch(() => {});
  }, [selectedClientId]);

  // Filtered properties
  const filtered = useMemo(() => {
    if (!data?.properties) return [];
    return data.properties.filter((p) => {
      if (search && !p.property_name.toLowerCase().includes(search.toLowerCase())) return false;
      if (statusFilter === 'attention' && !ACTION_STATES.includes(p.status)) return false;
      if (statusFilter === 'completed' && !['completed', 'review_complete'].includes(p.status)) return false;
      if (statusFilter === 'in_progress' && ['completed', 'review_complete', 'scheduled'].includes(p.status)) return false;
      if (monthFilter && p.audit_month !== monthFilter) return false;
      return true;
    });
  }, [data, search, statusFilter, monthFilter]);

  const paged = useMemo(() => filtered.slice((page - 1) * perPage, page * perPage), [filtered, page]);
  const totalPages = Math.ceil(filtered.length / perPage);

  // Reset page on filter change
  useEffect(() => setPage(1), [search, statusFilter, monthFilter]);

  // CSV export
  const exportCSV = () => {
    if (!filtered.length) return;
    const headers = ['Property', 'State', 'Status', 'Audit Month', 'Files Required', 'Files Received', 'Files Completed', 'Start Date', 'Contact Email', 'Days in Status'];
    const rows = filtered.map((p) => [
      p.property_name, p.state, p.status_label || p.status, p.audit_month ? MONTHS_SHORT[p.audit_month - 1] : '',
      p.files_required, p.files_received, p.files_completed, p.start_date || '', p.primary_contact_email || '', p.days_in_status || ''
    ]);
    const csv = [headers, ...rows].map((r) => r.map((v) => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `audit-tracker-${year}.csv`; a.click();
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" /></div>;
  if (!data?.audit) return (
    <div className={`p-8 text-center ${d ? 'text-gray-400' : 'text-gray-500'}`}>
      <Building2 size={48} className="mx-auto mb-4 opacity-40" />
      <p className="text-xl font-dm-serif mb-2">No Active Audits</p>
      <p className="text-sm font-rasa">Check back later for audit status updates.</p>
    </div>
  );

  const { kpis, pipeline, monthly_heatmap, monthly_completions, action_items, recent_changes } = data;

  const StatusBadge = ({ status }) => {
    const t = STATUS_THEME[status] || STATUS_THEME.scheduled;
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide ${t.light}`}>{t.label}</span>;
  };

  const DaysCell = ({ days }) => {
    if (days === null || days === undefined) return <span className={d ? 'text-gray-600' : 'text-gray-300'}>-</span>;
    const color = days > 21 ? 'text-red-500 font-bold' : days > 14 ? 'text-orange-500 font-semibold' : days > 7 ? (d ? 'text-gray-300' : 'text-gray-600') : (d ? 'text-gray-400' : 'text-gray-500');
    return <span className={`text-xs ${color}`}>{days}d</span>;
  };

  const totalChanges = Object.values(recent_changes || {}).reduce((a, b) => a + b, 0);

  return (
    <div className={`p-6 max-w-[1400px] mx-auto ${d ? 'text-gray-100' : 'text-gray-800'}`}>

      {/* ── Header ── */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-3xl font-dm-serif leading-tight">{data.audit.name || `${data.audit.year} Annual Audit`}</h1>
          <p className={`text-sm font-rasa mt-1 ${d ? 'text-gray-400' : 'text-gray-500'}`}>
            {kpis.total} properties &middot; {kpis.completion_rate}% complete
          </p>
        </div>
        <div className="flex items-center gap-3">
          {clients.length > 1 && (
            <select
              value={selectedClientId}
              onChange={(e) => { setSelectedClientId(e.target.value); setYear(''); }}
              className={`px-3 py-2 rounded-lg border text-sm font-rasa font-medium ${d ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-300 text-gray-700'}`}
            >
              {clients.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          )}
          <select value={year} onChange={(e) => setYear(e.target.value)} className={`px-3 py-2 rounded-lg border text-sm font-rasa font-medium ${d ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-300 text-gray-700'}`}>
            {(data.available_years || [year]).map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button
            onClick={() => setShowAskJPro(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-rasa font-medium bg-gradient-to-r from-blue-600 to-violet-600 text-white hover:from-blue-700 hover:to-violet-700 shadow-sm"
          >
            <Sparkles size={14} /> Ask JPro
          </button>
          <MoreDropdown isDark={d} onExport={exportCSV} onGuide={() => setShowGuide(true)} onTemplates={() => setShowTemplateSettings(true)} />
        </div>
      </div>

      {/* ── Change Banner ── */}
      {totalChanges > 0 && (
        <div className={`flex items-center gap-3 px-4 py-3 rounded-xl mb-6 ${d ? 'bg-blue-900/20 border border-blue-800/50' : 'bg-blue-50 border border-blue-200'}`}>
          <Zap size={16} className="text-blue-500 shrink-0" />
          <p className={`text-sm font-rasa flex-1 ${d ? 'text-blue-200' : 'text-blue-700'}`}>
            Since your last visit: {Object.entries(recent_changes).map(([status, count]) => `${count} ${STATUS_THEME[status]?.label || status}`).join(', ')}
          </p>
        </div>
      )}

      {/* ── KPI Cards (clickable) ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          {
            label: 'Completion Velocity',
            value: `${kpis.completion_rate}%`,
            sub: kpis.velocity_diff >= 0 ? `${kpis.velocity_diff} ahead of pace` : `${Math.abs(kpis.velocity_diff)} behind pace`,
            icon: kpis.velocity_diff >= 0 ? TrendingUp : TrendingDown,
            iconColor: kpis.velocity_diff >= 0 ? 'text-emerald-500' : 'text-orange-500',
            filter: 'completed',
          },
          {
            label: 'Awaiting Action',
            value: kpis.action_needed,
            sub: 'Properties need files',
            icon: AlertTriangle,
            iconColor: 'text-red-500',
            filter: 'attention',
          },
          {
            label: 'In Progress',
            value: (kpis.status_counts?.all_received || 0) + (kpis.status_counts?.in_review || 0) + (kpis.status_counts?.partially_received || 0),
            sub: `${kpis.avg_days_in_status || 0} avg days in status`,
            icon: Clock,
            iconColor: 'text-blue-500',
            filter: 'in_progress',
          },
          {
            label: 'Corrections Rate',
            value: `${kpis.corrections_rate}%`,
            sub: `${kpis.status_counts?.corrections_needed || 0} files pending`,
            icon: BarChart3,
            iconColor: 'text-violet-500',
            filter: null,
          },
        ].map((kpi) => {
          const Icon = kpi.icon;
          const isActive = statusFilter === kpi.filter;
          return (
            <button
              key={kpi.label}
              onClick={() => { setStatusFilter(isActive ? 'all' : (kpi.filter || 'all')); setMonthFilter(null); }}
              className={`text-left rounded-xl p-4 border transition-all ${
                isActive ? (d ? 'bg-gray-700 border-blue-500' : 'bg-blue-50 border-blue-500') : (d ? 'bg-gray-800 border-gray-700 hover:border-gray-600' : 'bg-white border-gray-200 hover:shadow-md')
              } shadow-sm`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className={`text-[10px] font-bold uppercase tracking-widest font-rasa ${d ? 'text-gray-400' : 'text-gray-500'}`}>{kpi.label}</span>
                <Icon size={16} className={kpi.iconColor} />
              </div>
              <div className="text-2xl font-dm-serif leading-none">{kpi.value}</div>
              <div className={`text-[11px] font-rasa mt-1.5 ${d ? 'text-gray-500' : 'text-gray-400'}`}>{kpi.sub}</div>
            </button>
          );
        })}
      </div>

      {/* ── Pipeline Funnel ── */}
      {pipeline && (
        <div className={`rounded-xl p-4 mb-6 ${d ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
          <div className={`text-[10px] font-bold uppercase tracking-widest font-rasa mb-3 ${d ? 'text-gray-400' : 'text-gray-500'}`}>Audit Pipeline</div>
          <div className="flex h-8 rounded-lg overflow-hidden gap-px">
            {pipeline.filter((s) => s.count > 0).map((s) => {
              const t = STATUS_THEME[s.status];
              const pct = kpis.total > 0 ? (s.count / kpis.total * 100) : 0;
              return (
                <button
                  key={s.status}
                  onClick={() => { setStatusFilter('all'); setMonthFilter(null); setSearch(''); }}
                  title={`${t?.label}: ${s.count} (${pct.toFixed(0)}%)`}
                  className={`${t?.bg || 'bg-gray-400'} transition-all hover:opacity-80 relative group`}
                  style={{ width: `${Math.max(pct, 2)}%` }}
                >
                  {pct > 8 && (
                    <span className="absolute inset-0 flex items-center justify-center text-white text-[10px] font-bold">{s.count}</span>
                  )}
                  <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 hidden group-hover:block z-10">
                    <div className={`text-[10px] px-2 py-1 rounded whitespace-nowrap font-rasa font-bold ${d ? 'bg-gray-700 text-gray-200' : 'bg-gray-800 text-white'}`}>
                      {t?.label}: {s.count}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3">
            {pipeline.filter((s) => s.count > 0).map((s) => {
              const t = STATUS_THEME[s.status];
              return (
                <div key={s.status} className="flex items-center gap-1.5">
                  <div className={`w-2.5 h-2.5 rounded-sm ${t?.bg || 'bg-gray-400'}`} />
                  <span className={`text-[11px] font-rasa ${d ? 'text-gray-400' : 'text-gray-500'}`}>{t?.label} ({s.count})</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Action Required + Chart Row ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Action Required */}
        <div className={`rounded-xl p-4 ${d ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
          <div className={`text-[10px] font-bold uppercase tracking-widest font-rasa mb-3 ${d ? 'text-gray-400' : 'text-gray-500'}`}>
            Action Required ({action_items?.length || 0})
          </div>
          {(!action_items || action_items.length === 0) ? (
            <div className={`text-center py-6 ${d ? 'text-gray-500' : 'text-gray-400'}`}>
              <CheckCircle2 size={24} className="mx-auto mb-2 text-emerald-500" />
              <p className="text-sm font-rasa">All caught up</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[240px] overflow-y-auto pr-1">
              {action_items.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setSelectedPropertyId(item.id)}
                  className={`w-full text-left p-3 rounded-lg transition-colors ${d ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-medium truncate ${d ? 'text-gray-200' : 'text-gray-700'}`}>
                        {item.property_name}
                        {item.days_in_status > 21 && <span className="ml-1.5 w-2 h-2 rounded-full bg-red-500 inline-block animate-pulse" />}
                      </div>
                      <div className={`text-[11px] font-rasa mt-0.5 ${d ? 'text-gray-400' : 'text-gray-500'}`}>
                        {item.files_received || 0}/{item.files_required || 0} files
                        {item.followup_count > 0 && ` · ${item.followup_count} follow-ups sent`}
                        {item.days_in_status && ` · ${item.days_in_status} days waiting`}
                      </div>
                    </div>
                    <DaysCell days={item.days_in_status} />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Completion Trajectory Chart */}
        <div className={`rounded-xl p-4 ${d ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
          <div className={`text-[10px] font-bold uppercase tracking-widest font-rasa mb-3 ${d ? 'text-gray-400' : 'text-gray-500'}`}>Completion Trajectory</div>
          {monthly_completions && (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={monthly_completions.map((m) => ({ ...m, name: MONTHS_SHORT[m.month - 1] }))} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
                <defs>
                  <linearGradient id="expectedGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#94a3b8" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#94a3b8" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="actualGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: d ? '#6b7280' : '#9ca3af' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: d ? '#6b7280' : '#9ca3af' }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: d ? '#1f2937' : '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontFamily: 'Rasa' }} />
                <Area type="monotone" dataKey="expected" stroke="#94a3b8" strokeDasharray="5 5" fill="url(#expectedGrad)" strokeWidth={1.5} name="Expected" />
                <Area type="monotone" dataKey="actual" stroke="#10b981" fill="url(#actualGrad)" strokeWidth={2} name="Actual" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* ── Monthly Heatmap ── */}
      {monthly_heatmap && (
        <div className={`rounded-xl p-4 mb-6 ${d ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
          <div className={`text-[11px] font-bold uppercase tracking-widest font-rasa mb-3 ${d ? 'text-gray-400' : 'text-gray-500'}`}>Properties by Audit Month</div>
          <div className="grid grid-cols-6 lg:grid-cols-12 gap-2">
            {monthly_heatmap.map((m) => {
              const isActive = monthFilter === m.month;
              const isFuture = m.active === false;
              const hue = m.total === 0 ? (d ? 'bg-gray-700' : 'bg-gray-100')
                : m.rate >= 80 ? (d ? 'bg-emerald-900/50' : 'bg-emerald-100')
                : m.rate >= 40 ? (d ? 'bg-blue-900/40' : 'bg-blue-50')
                : m.rate > 0 ? (d ? 'bg-orange-900/30' : 'bg-orange-50')
                : (d ? 'bg-gray-700' : 'bg-gray-100');
              return (
                <button
                  key={m.month}
                  onClick={() => setMonthFilter(isActive ? null : m.month)}
                  className={`rounded-lg p-2 text-center transition-all ${hue} ${isActive ? 'ring-2 ring-blue-500' : 'hover:ring-1 hover:ring-gray-400'}`}
                >
                  <div className={`text-[11px] font-bold font-rasa ${d ? 'text-gray-300' : 'text-gray-600'}`}>{MONTHS_SHORT[m.month - 1]}</div>
                  <div className={`text-base font-dm-serif leading-tight ${m.total === 0 ? (d ? 'text-gray-600' : 'text-gray-300') : ''}`}>{m.total}</div>
                  <div className={`text-[11px] font-rasa ${d ? 'text-gray-500' : 'text-gray-400'}`}>
                    {m.total > 0 ? `${m.rate}% done` : 'none'}
                  </div>
                  {m.action_needed > 0 && <div className="w-1.5 h-1.5 rounded-full bg-red-500 mx-auto mt-0.5" />}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Smart Filter ── */}
      <div className="mb-3">
        <SmartFilter
          audience="client"
          context="client"
          onApplyFilters={(aiFilters) => {
            if (Object.keys(aiFilters).length === 0) {
              setSearch(''); setStatusFilter('all'); setMonthFilter(null);
              return;
            }
            if (aiFilters.search) setSearch(aiFilters.search);
            if (aiFilters.status) {
              if (['completed', 'review_complete'].includes(aiFilters.status)) setStatusFilter('completed');
              else if (['files_requested', 'partially_received', 'unresponsive'].includes(aiFilters.status)) setStatusFilter('attention');
              else setStatusFilter('all');
            }
            if (aiFilters.audit_month) setMonthFilter(aiFilters.audit_month);
            if (aiFilters.quick === 'attention') setStatusFilter('attention');
            if (aiFilters.quick === 'overdue') setStatusFilter('attention');
          }}
        />
      </div>

      {/* ── Filter Bar ── */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search size={15} className={`absolute left-3 top-1/2 -translate-y-1/2 ${d ? 'text-gray-500' : 'text-gray-400'}`} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search property..."
            className={`w-full pl-9 pr-3 py-2 rounded-lg border text-sm font-rasa ${d ? 'bg-gray-800 border-gray-700 text-gray-200 placeholder-gray-500' : 'bg-white border-gray-300 text-gray-700 placeholder-gray-400'} focus:outline-none focus:ring-2 focus:ring-blue-500/50`}
          />
        </div>
        <div className={`flex rounded-lg border overflow-hidden ${d ? 'border-gray-700' : 'border-gray-300'}`}>
          {[
            { key: 'all', label: 'All' },
            { key: 'attention', label: `Action (${kpis.action_needed})` },
            { key: 'in_progress', label: 'In Progress' },
            { key: 'completed', label: `Done (${kpis.completed})` },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => { setStatusFilter(f.key); setMonthFilter(null); }}
              className={`px-3 py-1.5 text-xs font-rasa font-medium transition-colors ${
                statusFilter === f.key
                  ? (d ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white')
                  : (d ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-white text-gray-500 hover:bg-gray-50')
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        {monthFilter && (
          <button onClick={() => setMonthFilter(null)} className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-rasa ${d ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
            {MONTHS_FULL[monthFilter - 1]} <X size={12} />
          </button>
        )}
        <span className={`ml-auto text-[11px] font-rasa font-medium ${d ? 'text-gray-500' : 'text-gray-400'}`}>
          {filtered.length} properties
        </span>
      </div>

      {/* ── Property Table ── */}
      <div className={`rounded-xl border overflow-hidden shadow-sm ${d ? 'border-gray-700' : 'border-gray-200'}`}>
        <table className="w-full">
          <thead>
            <tr className={d ? 'bg-gray-800' : 'bg-gray-50'}>
              {['Property', 'Status', 'Month', 'Files', 'Days', 'Contact'].map((h) => (
                <th key={h} className={`px-4 py-2.5 text-left text-[10px] font-bold uppercase tracking-widest font-rasa ${d ? 'text-gray-400' : 'text-gray-500'}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paged.length === 0 ? (
              <tr><td colSpan={6} className={`px-4 py-8 text-center text-sm font-rasa ${d ? 'text-gray-500' : 'text-gray-400'}`}>No properties match filters</td></tr>
            ) : paged.map((p) => (
              <tr
                key={p.id}
                onClick={() => setSelectedPropertyId(p.id)}
                className={`border-b cursor-pointer transition-colors ${
                  selectedPropertyId === p.id ? (d ? 'bg-gray-800/80' : 'bg-blue-50/50') : ''
                } ${d ? 'border-gray-700/50 hover:bg-gray-800/50' : 'border-gray-100 hover:bg-gray-50'}`}
              >
                <td className="px-4 py-3">
                  <div className={`text-sm font-medium ${d ? 'text-gray-200' : 'text-gray-800'}`}>
                    {p.property_name}
                    {ACTION_STATES.includes(p.status) && p.days_in_status > 14 && (
                      <span className="ml-1.5 w-1.5 h-1.5 rounded-full bg-red-500 inline-block" />
                    )}
                  </div>
                  {p.state && <span className={`text-[11px] font-rasa ${d ? 'text-gray-500' : 'text-gray-400'}`}>{p.state}</span>}
                </td>
                <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                <td className={`px-4 py-3 text-xs font-rasa ${d ? 'text-gray-400' : 'text-gray-500'}`}>{p.audit_month ? MONTHS_SHORT[p.audit_month - 1] : '-'}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-14 h-1.5 rounded-full ${d ? 'bg-gray-700' : 'bg-gray-200'}`}>
                      <div className="h-1.5 rounded-full bg-blue-500 transition-all" style={{ width: `${p.files_required > 0 ? Math.min(100, (p.files_received || 0) / p.files_required * 100) : 0}%` }} />
                    </div>
                    <span className="text-[11px] font-rasa font-medium whitespace-nowrap">{p.files_received || 0}/{p.files_required || 0}</span>
                  </div>
                </td>
                <td className="px-4 py-3"><DaysCell days={p.days_in_status} /></td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  {p.primary_contact_email ? (
                    <button
                      onClick={() => setComposeEmail({ property: p, contact: { email: p.primary_contact_email, name: p.primary_contact_name } })}
                      className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded border font-rasa ${d ? 'border-gray-600 text-gray-400 hover:bg-gray-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}
                    >
                      <Mail size={9} /> Email
                    </button>
                  ) : <span className={`text-[10px] ${d ? 'text-gray-600' : 'text-gray-300'}`}>-</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        <Pagination currentPage={page} totalPages={totalPages} totalCount={filtered.length} perPage={perPage} onPageChange={setPage} />
      </div>

      {/* ── Ask JPro Modal ── */}
      {showAskJPro && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4 bg-black/40" onClick={() => setShowAskJPro(false)}>
          <div className="w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <AskJPro
              portfolioData={{ ...kpis, action_items: action_items }}
              mode="portfolio"
              audience="client"
              onClose={() => setShowAskJPro(false)}
            />
          </div>
        </div>
      )}

      {/* ── Property Side Panel ── */}
      {selectedPropertyId && (
        <ClientPropertySidePanel
          propertyId={selectedPropertyId}
          onClose={() => setSelectedPropertyId(null)}
          onEmailClick={(prop, contact) => setComposeEmail({ property: prop, contact })}
        />
      )}

      {/* ── Email Compose Modal ── */}
      <EmailComposeModal
        isOpen={!!composeEmail}
        onClose={() => setComposeEmail(null)}
        property={composeEmail?.property}
        contact={composeEmail?.contact}
        clientTemplates={clientTemplates}
      />

      {/* ── Email Template Settings ── */}
      {showTemplateSettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/40" onClick={() => setShowTemplateSettings(false)}>
          <div className="w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
            <ClientEmailTemplateSettings onClose={() => { setShowTemplateSettings(false); apiGet('/client/audit_tracker/email_templates').then((r) => r.ok ? r.json() : { templates: {} }).then((d) => setClientTemplates(d.templates || {})).catch(() => {}); }} />
          </div>
        </div>
      )}

      {/* Footer */}
      <div className={`flex items-center justify-between mt-4 px-1 ${d ? 'text-gray-600' : 'text-gray-300'}`}>
        <span className="text-[11px] font-rasa">Powered by JPro</span>
        <span className="text-[11px] font-rasa">Data as of {new Date().toLocaleDateString()}</span>
      </div>

      {/* Client Feature Guide */}
      <InfoModal isOpen={showGuide} onClose={() => setShowGuide(false)} title="Audit Dashboard Guide">
        <div className="space-y-4 text-sm">
          <section>
            <h4 className="font-semibold text-base mb-2">Welcome to Your Audit Dashboard</h4>
            <p className="text-gray-600 dark:text-gray-400">
              This dashboard gives you real-time visibility into your property audit portfolio. Here's what you can do:
            </p>
          </section>

          <section>
            <h4 className="font-semibold text-base mb-2">Dashboard Features</h4>
            <ul className="list-disc list-inside space-y-1.5 ml-2 text-gray-600 dark:text-gray-400">
              <li><strong>KPI Cards</strong> — See completion velocity, properties needing action, review progress, and corrections rate. Click any card to filter the table below.</li>
              <li><strong>Pipeline Bar</strong> — The colored bar shows all properties across lifecycle stages. Hover to see counts.</li>
              <li><strong>Action Required</strong> — Properties that need attention from your team, sorted by urgency.</li>
              <li><strong>Completion Trajectory</strong> — Chart showing actual completions vs expected pace.</li>
              <li><strong>Monthly Overview</strong> — Click any month to filter properties scheduled for that month.</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold text-base mb-2">Filtering & Search</h4>
            <ul className="list-disc list-inside space-y-1.5 ml-2 text-gray-600 dark:text-gray-400">
              <li><strong>Smart Filter</strong> — Type a question like "properties that need attention" or "completed in March" and JPro AI will filter for you.</li>
              <li><strong>Status Pills</strong> — Quick filter: All, Action Needed, In Progress, Done.</li>
              <li><strong>Year Selector</strong> — Switch between audit years to see historical data.</li>
              <li><strong>Search</strong> — Type a property name to find it quickly.</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold text-base mb-2">Property Details</h4>
            <p className="text-gray-600 dark:text-gray-400 mb-2">
              Click any property row to open a <strong>side panel</strong> with detailed information:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-2 text-gray-600 dark:text-gray-400">
              <li><strong>Overview</strong> — Status, progress bars, property details, contacts with email buttons</li>
              <li><strong>Files</strong> — Individual tenant file status table with correction details</li>
              <li><strong>Timeline</strong> — Activity history: status changes, files received, emails sent</li>
              <li><strong>Ask JPro</strong> — AI analysis of this specific property</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold text-base mb-2">Emailing Properties</h4>
            <ul className="list-disc list-inside space-y-1 ml-2 text-gray-600 dark:text-gray-400">
              <li>Click <strong>Email</strong> on any property row or in the side panel</li>
              <li>A compose window opens with a <strong>pre-loaded template</strong> based on the property status</li>
              <li>Edit the subject and body as needed, then click <strong>"Open in Email"</strong></li>
              <li>The email opens in your own email client (Outlook, Gmail) with your branding and signature</li>
              <li>Customize default templates via <strong>More menu (...) → Email Templates</strong></li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold text-base mb-2">Ask JPro</h4>
            <p className="text-gray-600 dark:text-gray-400">
              Click the <strong>"Ask JPro"</strong> button to chat with our JPRO assistant. Ask questions about your portfolio like "Which properties are behind schedule?" or "What are the current bottlenecks?" JPro analyzes your real-time data and gives actionable insights.
            </p>
          </section>

          <section>
            <h4 className="font-semibold text-base mb-2">Export</h4>
            <p className="text-gray-600 dark:text-gray-400">
              Click <strong>Export</strong> to download the current filtered view as a CSV file for your own reporting.
            </p>
          </section>

          <section>
            <h4 className="font-semibold text-base mb-2">Understanding Statuses</h4>
            <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded border border-blue-200 dark:border-blue-800 text-xs space-y-1">
              <p><strong>Scheduled</strong> — Audit planned but not yet started</p>
              <p><strong>Setup Requested</strong> — We've asked the property for documents</p>
              <p><strong>Pending Upload</strong> — Waiting for the property to submit files</p>
              <p><strong>Partial Upload</strong> — Some files received, more needed</p>
              <p><strong>All Received</strong> — All files in, queued for review</p>
              <p><strong>In Review</strong> — Files being reviewed</p>
              <p><strong>Final Report Sent</strong> — Audit complete, report delivered</p>
            </div>
          </section>

          <section>
            <h4 className="font-semibold text-base mb-2">Tips</h4>
            <ul className="list-disc list-inside space-y-1 ml-2 text-gray-600 dark:text-gray-400">
              <li>Check the <strong>Action Required</strong> section daily — it shows what needs attention now</li>
              <li>Properties with longer wait times appear higher in the urgency list</li>
              <li>Respond quickly to file requests to avoid escalation follow-ups</li>
              <li>Use the <strong>Email</strong> button to send pre-loaded emails — customize templates once in Email Templates settings</li>
              <li>Use the trajectory chart to see if you're ahead or behind schedule</li>
            </ul>
          </section>
        </div>
      </InfoModal>
    </div>
  );
};

export default ClientAuditDashboard;
